import Skeleton from '../skeleton/Skeleton';
import {
  StyledAccounts,
  StyledAccountsTable,
  StyledLimitsTable,
  StyledLinkedinCard,
  StyledLinkedinLayout,
  StyledStatCard,
} from './Accounts.styles';

const MAILBOX_SKELETON_ROWS = 10;
const LIMIT_SKELETON_ROWS = 5;

/** The LinkedIn tab's shape: profile rail, profile card, limits table. */
const LinkedinTabSkeleton = () => (
  <StyledLinkedinLayout>
    <aside className="li-sidebar">
      <Skeleton width={120} height={14} radius="6px" margin="0 4px 12px" />
      <Skeleton width="100%" height={36} radius="999px" margin="0 0 12px" />
      {Array.from({ length: 3 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px' }}>
          <Skeleton width={32} height={32} circle />
          <Skeleton width="60%" height={13} radius="6px" />
        </div>
      ))}
    </aside>
    <div className="li-detail">
      <StyledLinkedinCard>
        <Skeleton width="100%" height={140} radius="0" />
        <div className="li-body">
          <div className="li-avatar-row">
            <div className="li-avatar-ring">
              <Skeleton width={116} height={116} circle />
            </div>
            <Skeleton width={110} height={36} radius="18px" />
          </div>
          <Skeleton width={220} height={24} radius="8px" margin="16px 0 0" resp />
          <Skeleton width={320} height={14} radius="6px" margin="10px 0 0" resp />
          <Skeleton width={260} height={14} radius="6px" margin="18px 0 0" resp />
        </div>
      </StyledLinkedinCard>
      <div>
        <Skeleton width={150} height={20} radius="6px" margin="0 0 12px" />
        <StyledLimitsTable>
          <div className="limits-head">
            {Array.from({ length: 5 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={i} width="60%" height={11} radius="6px" />
            ))}
          </div>
          {Array.from({ length: LIMIT_SKELETON_ROWS }).map((_, r) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={r} className="limits-row">
              <Skeleton width="70%" height={14} radius="6px" />
              <Skeleton width={80} height={36} radius="10px" />
              <Skeleton width={30} height={14} radius="6px" />
              <Skeleton width={30} height={14} radius="6px" />
              <Skeleton width={100} height={36} radius="10px" />
            </div>
          ))}
        </StyledLimitsTable>
      </div>
    </div>
  </StyledLinkedinLayout>
);

/** The email tab's shape: stat cards, provider cards, mailbox table. */
const EmailTabSkeleton = () => (
  <div className="accounts-stack">
    <div className="stats-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <StyledStatCard key={i}>
          <Skeleton width={90} height={12} radius="6px" />
          <Skeleton width={40} height={24} radius="8px" margin="8px 0 0" />
        </StyledStatCard>
      ))}
    </div>
    <div className="providers-grid">
      {Array.from({ length: 3 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <StyledStatCard key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Skeleton width={40} height={40} radius="14px" />
          <div style={{ flex: 1 }}>
            <Skeleton width="55%" height={14} radius="6px" />
            <Skeleton width="75%" height={12} radius="6px" margin="6px 0 0" />
          </div>
        </StyledStatCard>
      ))}
    </div>
    <StyledAccountsTable>
      <table>
        <thead>
          <tr>
            {Array.from({ length: 6 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <th key={i}>
                <Skeleton width={56} height={11} radius="6px" />
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: MAILBOX_SKELETON_ROWS }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i} className="skeleton-row">
              <td>
                <Skeleton width={55} height={13} radius="6px" margin="0 0 6px" />
                <Skeleton width={80} height={11} radius="6px" />
              </td>
              <td>
                <Skeleton width={60} height={12} radius="6px" />
              </td>
              <td>
                <Skeleton width={70} height={20} radius="999px" />
              </td>
              <td>
                <Skeleton width={50} height={12} radius="6px" margin="0 0 6px" />
                <Skeleton width={85} height={6} radius="999px" />
              </td>
              <td>
                <Skeleton width={45} height={12} radius="6px" />
              </td>
              <td>
                <Skeleton width={65} height={12} radius="6px" />
              </td>
              <td>
                <Skeleton width={90} height={28} radius="14px" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </StyledAccountsTable>
  </div>
);

/**
 * The whole page's shape, shown until both tabs' accounts have arrived.
 * `tab` is the tab being drawn: 'linkedin' (the default, as on the page) or
 * 'email' (anything that is not 'linkedin' draws the email tab).
 */
const AccountsSkeleton = ({ tab = 'linkedin' }) => (
  <StyledAccounts aria-busy="true">
    <main className="accounts-main">
      <div className="accounts-head">
        <div>
          <Skeleton width={130} height={28} radius="8px" resp />
          <Skeleton width={380} height={14} radius="6px" margin="8px 0 0" resp />
        </div>
        {tab !== 'linkedin' && <Skeleton width={160} height={36} radius="18px" resp />}
      </div>
      <div className="accounts-tabs">
        <Skeleton width={180} height={32} radius="14px" margin="0 4px 0 0" resp />
        <Skeleton width={160} height={32} radius="14px" resp />
      </div>
      <div className="accounts-section">{tab === 'linkedin' ? <LinkedinTabSkeleton /> : <EmailTabSkeleton />}</div>
    </main>
  </StyledAccounts>
);

export default AccountsSkeleton;
